import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { resources, ns, defaultNS } from "./resources";

export const SUPPORTED_LANGUAGES = ["pt-BR", "en"] as const;
export type AppLanguage = (typeof SUPPORTED_LANGUAGES)[number];
export const DEFAULT_LANGUAGE: AppLanguage = "pt-BR";

// Chave usada pelo detector no localStorage. Mantida estável para lembrar a
// escolha do usuário entre sessões.
export const LANGUAGE_STORAGE_KEY = "taskletto_lang";

/**
 * Inicializa a instância global do i18next (idempotente).
 *
 * - No cliente: usa o LanguageDetector (localStorage → navigator → fallback).
 * - No SSR/prerender: passe `ssrLng` para fixar o idioma; o detector do
 *   navegador não roda fora do browser.
 *
 * Como os recursos são bundled (síncronos), a troca de idioma é aplicada
 * imediatamente — importante para o `renderToString` da landing.
 */
export function initI18n(ssrLng?: AppLanguage) {
  if (i18n.isInitialized) return i18n;

  const instance = i18n.use(initReactI18next);
  if (!ssrLng) instance.use(LanguageDetector);

  instance.init({
    resources,
    ns: ns as unknown as string[],
    defaultNS,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    lng: ssrLng,
    // Recursos são bundled (síncronos): inicializa sem adiar para o próximo tick,
    // garantindo que o primeiro render (SSR/prerender) já tenha as traduções
    // prontas — senão o primeiro `t` devolveria a própria chave.
    initAsync: false,
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      // Somente leitura: quem grava a preferência é `setLanguagePreference`.
      // Se o detector cacheasse sozinho, a troca temporária feita pela landing
      // (`/en` → inglês, apenas para exibir) viraria preferência permanente e o
      // app inteiro ficaria em inglês no próximo F5.
      caches: [],
    },
    react: { useSuspense: false },
  });

  if (!ssrLng) appLanguage = normalizeLanguage(i18n.resolvedLanguage || i18n.language);

  return i18n;
}

/** Normaliza qualquer tag ("en-US", "pt", …) para um idioma suportado. */
export function normalizeLanguage(lng: string | undefined): AppLanguage {
  return lng?.toLowerCase().startsWith("en") ? "en" : DEFAULT_LANGUAGE;
}

// Preferência de idioma do app (dashboard, auth, ajustes). Resolvida no init a
// partir de localStorage → navegador e alterada só por escolha explícita do
// usuário. A landing troca o idioma pela URL sem tocar nisto.
let appLanguage: AppLanguage = DEFAULT_LANGUAGE;

/** Idioma que o app (fora da landing) deve usar. */
export function getAppLanguage(): AppLanguage {
  return appLanguage;
}

// main.tsx registra aqui a sincronização da preferência com o backend (usada em
// e-mails/push). Fica como callback porque `services/api` importa este módulo —
// importá-lo de volta criaria um ciclo.
let onPreferenceChange: ((lng: AppLanguage) => void) | undefined;

export function onLanguagePreferenceChange(cb: (lng: AppLanguage) => void) {
  onPreferenceChange = cb;
}

/**
 * Registra a escolha explícita do usuário: aplica, persiste e notifica.
 * Use sempre que a troca partir de uma ação do usuário (seletor PT/EN).
 */
export function setLanguagePreference(lng: AppLanguage) {
  appLanguage = lng;
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lng);
  } catch {
    // localStorage indisponível (modo privado/quota): segue sem persistir.
  }
  i18n.changeLanguage(lng);
  onPreferenceChange?.(lng);
}

/**
 * Aplica o idioma do URL da landing — exibição apenas, sem virar preferência.
 * `/` é sempre pt-BR e `/en` sempre inglês, casando com as páginas estáticas
 * pré-renderizadas (evita divergência na hidratação) e mantendo o hreflang.
 */
export function applyLandingLanguage(lng: AppLanguage) {
  if (i18n.language !== lng) i18n.changeLanguage(lng);
}

/** Ao sair da landing, volta para a preferência do usuário. */
export function restoreAppLanguage() {
  if (i18n.language !== appLanguage) i18n.changeLanguage(appLanguage);
}

// Mantém o atributo <html lang> sincronizado com o idioma ativo (acessibilidade
// e SEO). Chamado no cliente após init e a cada troca de idioma.
export function syncHtmlLang(lng: string) {
  if (typeof document !== "undefined") {
    document.documentElement.lang = lng;
  }
}

export default i18n;
